'use client';

import { type ReactNode, useRef, useState } from 'react';

import {
  Accordion,
  type AccordionProps,
} from '@/app/components/text-and-data-display/accordion';
import { cn } from '@/app/utils/cn';

export interface AccordionGroupItemProps {
  title: string;
  content: React.ReactNode;
  value: string;
  leftIcon?: ReactNode;
}

export interface AccordionGroupProps extends Omit<
  AccordionProps,
  'value' | 'content'
> {
  data: AccordionGroupItemProps[];
  defaultValue?: string;
  itemClassName?: string;
  enableScrollIntoView?: boolean;
}

export const AccordionGroup = ({
  data,
  defaultValue,
  className,
  itemClassName,
  contentClassName,
  wrapperClassName,
  enableScrollIntoView = true,
}: AccordionGroupProps) => {
  const accordionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [currentValue, setCurrentValue] = useState<string>(defaultValue || '');

  const handleChange = (value: string) => {
    if (!enableScrollIntoView) {
      return setCurrentValue((prev) => (prev === value ? '' : value));
    }

    setCurrentValue((prev) => {
      const targetElement = accordionRefs.current[value];
      if (targetElement) {
        requestAnimationFrame(() => {
          targetElement.scrollIntoView({
            behavior: 'smooth',
          });
        });
      }
      return prev === value ? '' : value;
    });
  };

  return (
    <div className={cn('space-y-2', className)}>
      {data.map(({ title, content, value, leftIcon }, index) => (
        <Accordion
          key={value}
          ref={(el) => {
            accordionRefs.current[value] = el;
          }}
          wrapperClassName={cn('scroll-mt-16', wrapperClassName)}
          className={itemClassName}
          contentClassName={contentClassName}
          value={value}
          title={title}
          content={content}
          leftIcon={leftIcon}
          isOpen={currentValue === value}
          onChange={handleChange}
          test={`accordion-item-${index}`}
        />
      ))}
    </div>
  );
};
